"use client"

import { BlockchainsMoleculeReatom } from "@/app/blockchains/blockchain-molecule.reatom"
import { ModalErrorMessage } from "@/app/error"
import { useConnectStatus } from "@/app/nft/order/widget/actions/use-connect-status"
import { BalanceBlockchainScope } from "@/app/profiles/balance/balance-molecule.reatom"
import { useWalletConnect } from "@/app/wallet-connect/hooks"
import type { Nft } from "@anaconda/core/nft/types"
import { Trans } from "@lingui/react/macro"
import {
  Button,
  type ButtonProps,
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { ScopeProvider, useMolecule } from "bunshi/react"
import { Suspense, useCallback, useState } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { EvmItemOfferDialogContent } from "./evm-item-offer-dialog-content.reatom"
import { SolanaItemOfferDialogContent } from "./solana-item-offer-dialog-content.reatom"

interface Props {
  nft: Nft
  trigger?: Omit<ButtonProps, "onClick" | "children">
}

export const ItemOfferDialog = reatomComponent<Props>(({ nft, trigger }) => {
  const [isOpened, setOpen] = useState(false)
  const { status } = useWalletConnect()
  const connect = useConnectStatus(() => {})
  const { reatomIsEvmBlockchain } = useMolecule(BlockchainsMoleculeReatom)
  const isEvmBlockchain = reatomIsEvmBlockchain()(nft.collection.blockchain)

  const handleOpen = useCallback(
    (open: boolean) => {
      if (status === "connected") {
        setOpen(open)
      } else {
        connect()
      }
    },
    [status, connect],
  )

  return (
    <Dialog onOpenChange={handleOpen} open={isOpened}>
      <DialogTrigger asChild id="item-offer-dialog">
        <Button variant="secondary" {...trigger}>
          <Trans>Make offer</Trans>
        </Button>
      </DialogTrigger>
      <DialogContent
        className="max-w-[396px] overflow-x-hidden"
        aria-describedby="item-offer-dialog"
      >
        <ErrorBoundary fallback={<ModalErrorMessage />}>
          <Suspense fallback={null}>
            <ScopeProvider
              scope={BalanceBlockchainScope}
              value={nft.collection.blockchain}
            >
              {isEvmBlockchain ? (
                <EvmItemOfferDialogContent nft={nft} onOpenChange={setOpen} />
              ) : (
                <SolanaItemOfferDialogContent
                  nft={nft}
                  onOpenChange={setOpen}
                />
              )}
            </ScopeProvider>
          </Suspense>
        </ErrorBoundary>
      </DialogContent>
    </Dialog>
  )
}, "ItemOfferDialog")
